/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { ApiService } from './api';

import { UserManager } from './managers/user.manager';

import { Horse } from 'src/models/horse';
import { Account } from 'src/models/account';
import { Trade } from 'src/models/trade';
import { AccountFavorite } from 'src/models/account-favorite';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  navigateToAfterAuthUrl: string;

  constructor(
    private api: ApiService,
    private userManager: UserManager) { }

  async updateUserPassword(id, password) {
    return await this.api.putWithAuth(
      this.api.endpoints.users +
        '/updatePassword?id=' +
        id +
        '&password=' +
        password
    );
  }

  async getMemberships(accountId: number) {
    return await await this.api.getWithAuth(
      `${this.api.endpoints.users}/getMemberships?accountId=${accountId}`
    );
  }

  async getUser(userId: number, setAppAccount = false) {
    const data = await this.api.getWithAuth<Account>(
      this.api.endpoints.users + '/getAccount?id=' + userId
    );
    if (data && setAppAccount) {
      this.userManager.receiveAccount(data);
    } else {
      return data;
    }
  }

  async updateAccount(user: any) {
    return await this.api.putWithAuth(
      this.api.endpoints.users + '/updateAccount?id=' + user.id,
      user
    );
  }

  async updateMemberships(accountMemberships: any): Promise<any> {
    return await this.api.postWithAuth(
      this.api.endpoints.users + '/updateMemberships',
      accountMemberships
    );
  }

  async deactivateAccount(accountId: number) {
    return await this.api.deleteWithAuth(
      `${this.api.endpoints.users}/DeactivateAccount?accountId=${accountId}`
    );
  }

  async activateAccount(userId: number, organizationId: number) {
    return await this.api.putWithAuth(
      this.api.endpoints.users +
        '/activateAccount?id=' +
        userId +
        '&organizationId=' +
        organizationId
    );
  }

  async updateStaffAccounts(users: any) {
    return await this.api.putWithAuth(
      this.api.endpoints.users + '/updateStaffAccounts',
      users
    );
  }

  async addUser(user: any) {
    return await this.api.postWithoutAuth<Account>(
      this.api.endpoints.users + '/createFromApp',
      user
    );
  }

  async createYouth(user: any) {
    return await this.api.postWithAuth<Account>(
      this.api.endpoints.users + '/createYouth',
      user
    );
  }

  async addUserFromStaff(user: any) {
    return await this.api.postWithoutAuth(
      this.api.endpoints.users + '/createFromStaff',
      user
    );
  }

  async getHorses(accountId) {
    return await this.api.getWithAuth(
      this.api.endpoints.users + '/getHorses' + '?accountId=' + accountId
    );
  }

  async addHorse(accountId: number, horse: Horse) {
    return await this.api.postWithAuth(
      this.api.endpoints.users + '/addHorse' + '?accountId=' + accountId,
      horse
    );
  }

  async createNotification(accountId: number, rodeoEventId: number) {
    return await this.api.postWithAuth(
      this.api.endpoints.users +
        '/createNotification' +
        '?accountId=' +
        accountId +
        '&rodeoEventId=' +
        rodeoEventId
    );
  }

  async deleteNotification(accountId: number, rodeoEventId: number) {
    return await this.api.postWithAuth(
      this.api.endpoints.users +
        '/deleteNotification' +
        '?accountId=' +
        accountId +
        '&rodeoEventId=' +
        rodeoEventId
    );
  }

  async createTradeRequest(
    accountId: number,
    requestorRunId: number,
    targetRunId: number
  ) {
    return await this.api.postWithAuth(
      this.api.endpoints.users +
        '/createTradeRequest' +
        '?accountId=' +
        accountId +
        '&requestorRunId=' +
        requestorRunId +
        '&targetRunId=' +
        targetRunId
    );
  }

  async confirmTrade(accountId: number, trade: Trade) {
    return await this.api.postWithAuth(
      this.api.endpoints.users + '/confirmTrade' + '?accountId=' + accountId,
      trade
    );
  }

  async cancelTrade(accountId: number, trade: Trade) {
    return await this.api.postWithAuth(
      this.api.endpoints.users + '/cancelTrade' + '?accountId=' + accountId,
      trade
    );
  }

  async markNotificationsRead(accountId: number) {
    return await this.api.postWithAuth(
      this.api.endpoints.users +
        '/markNotificationsRead' +
        '?accountId=' +
        accountId
    );
  }

  async getUserTrades(accountId: number) {
    return await this.api.postWithAuth(
      this.api.endpoints.users + '/getUserTrades' + '?accountId=' + accountId
    );
  }

  async mergeAccounts(mergedUser, firstAccountId, secondAccountId) {
    return await this.api.postWithAuth(
      this.api.endpoints.users +
        '/mergeAccounts?' +
        'firstAccountId=' +
        firstAccountId +
        '&secondAccountId=' +
        secondAccountId,
      mergedUser
    );
  }

  async getAccountRodeoEvents(accountId: number) {
    const data = await this.api.getWithAuth(
      `${this.api.endpoints.users}/getAccountRodeoEvents?id=${accountId}`
    );

    return data;
  }
}
